import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { LoadingSpinner } from "../LoadingSpinner";
import { ErrorState } from "../ErrorState";
import { EmptyState } from "../EmptyState";
import { ConfirmDialog } from "../ConfirmDialog";
import { listAssignments, addAssignment, removeAssignment } from "../../services/event.service";
import { getApiErrorMessage } from "../../lib/apiClient";
import type { EventAssignmentRole } from "../../types";

export function AssignmentsPanel({ eventId }: { eventId: string }): JSX.Element {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["events", eventId, "assignments"],
    queryFn: () => listAssignments(eventId),
  });

  const [email, setEmail] = useState("");
  const [role, setRole] = useState<EventAssignmentRole>("MANAGER");
  const [removeTarget, setRemoveTarget] = useState<{ userId: string; name: string } | null>(null);

  const addMutation = useMutation({
    mutationFn: () => addAssignment(eventId, email, role),
    onSuccess: () => {
      toast.success("Assignment added");
      setEmail("");
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "assignments"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeAssignment(eventId, userId),
    onSuccess: () => {
      toast.success("Assignment removed");
      setRemoveTarget(null);
      queryClient.invalidateQueries({ queryKey: ["events", eventId, "assignments"] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err)),
  });

  const handleSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Email is required");
      return;
    }
    addMutation.mutate();
  };

  return (
    <div>
      <form onSubmit={handleSubmit} className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-end">
        <div className="flex-1">
          <label className="label" htmlFor="assign-email">
            User email (must already have a User account)
          </label>
          <input
            id="assign-email"
            type="email"
            className="input"
            placeholder="manager@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="assign-role">
            Role
          </label>
          <select
            id="assign-role"
            className="input"
            value={role}
            onChange={(e) => setRole(e.target.value as EventAssignmentRole)}
          >
            <option value="MANAGER">Manager</option>
            <option value="STAFF">Staff</option>
          </select>
        </div>
        <button type="submit" className="btn-primary" disabled={addMutation.isPending}>
          {addMutation.isPending ? "Assigning..." : "Assign"}
        </button>
      </form>

      {isLoading && <LoadingSpinner />}
      {isError && <ErrorState message={getApiErrorMessage(error)} />}
      {data && data.length === 0 && <EmptyState title="No one assigned" description="Assign a Manager to this event." />}

      {data && data.length > 0 && (
        <div className="card divide-y divide-slate-100 dark:divide-slate-700 animate-fade-in">
          {data.map((assignment) => (
            <div key={assignment.id} className="flex items-center justify-between p-4">
              <div>
                <p className="font-medium text-slate-900 dark:text-slate-100">{assignment.user.name}</p>
                <p className="text-sm text-slate-500 dark:text-slate-400">{assignment.user.email}</p>
              </div>
              <div className="flex items-center gap-4">
                <span className="badge bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300">{assignment.roleInEvent}</span>
                <button
                  type="button"
                  className="text-sm font-medium text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300 transition-colors duration-150"
                  onClick={() => setRemoveTarget({ userId: assignment.userId, name: assignment.user.name })}
                >
                  Remove
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!removeTarget}
        title="Remove assignment"
        message={`Remove ${removeTarget?.name} from this event?`}
        confirmLabel="Remove"
        danger
        isLoading={removeMutation.isPending}
        onCancel={() => setRemoveTarget(null)}
        onConfirm={() => removeTarget && removeMutation.mutate(removeTarget.userId)}
      />
    </div>
  );
}
